import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type {
  ComposeServiceInfo, DomainVerifyInstructions, EnvVar, Exposure,
  InstanceComposeDetail, InstanceDetail, ServiceUser,
} from '@hub/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import {
  ActionButton, Alert, Card, EmptyRow, Loading, Pill, SectionTitle, StateDot, fmtDate, useFlash,
} from '../components/ui';

/** Values that look secret are masked in the table until revealed. */
const SENSITIVE = /SECRET|TOKEN|PASSWORD|KEY/i;

export function InstanceSettingsPage() {
  const { id } = useParams<{ id: string }>();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  const [instance, setInstance] = useState<InstanceDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setInstance(await api.get<InstanceDetail>(`/api/instances/${id}`));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the instance');
    }
  }, [id]);

  useEffect(() => { void load(); }, [load]);

  if (error) return <Alert tone="error">{error}</Alert>;
  if (!instance) return <Loading />;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>{instance.name}</h2>
          <StateDot state={instance.status} />
        </div>
        <Link to="/dashboard" className="btn-ghost" style={{ padding: '0.25rem 0.75rem', fontSize: '0.8rem' }}>
          Back to dashboard
        </Link>
      </div>

      <OverviewCard instance={instance} />

      {instance.kind === 'compose' ? (
        <>
          <ExposuresCard instance={instance} isAdmin={isAdmin} onChanged={load} />
          <ComposeFileCard instanceId={instance.id} />
        </>
      ) : (
        <>
          <SubdomainCard instance={instance} onChanged={load} />
          <ExternalDomainCard instance={instance} onChanged={load} />
          <ServiceUsersCard instanceId={instance.id} />
          <EnvVarsCard instanceId={instance.id} />
          {isAdmin && <ResourceLimitsCard instance={instance} onChanged={load} />}
        </>
      )}

      {isAdmin && <ExpirationCard instance={instance} onChanged={load} />}
      <DangerZoneCard instance={instance} onDeleted={() => navigate('/dashboard')} />
    </>
  );
}

function OverviewCard({ instance }: { instance: InstanceDetail }) {
  const compose = instance.kind === 'compose';
  return (
    <Card style={{ marginBottom: '1.5rem' }}>
      <SectionTitle>Overview</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', fontSize: '0.85rem' }}>
        <div><div className="muted">Subdomain</div><div className="mono">{instance.subdomain}</div></div>
        <div>
          <div className="muted">{compose ? 'Type' : 'Image'}</div>
          <div className="mono">{compose ? 'compose stack' : instance.product}</div>
        </div>
        {compose
          ? <div><div className="muted">Project</div><div className="mono">{instance.compose_project ?? '-'}</div></div>
          : <div><div className="muted">Host port</div><div className="mono">{instance.host_port ?? '-'}</div></div>}
        <div><div className="muted">Created</div><div>{fmtDate(instance.created_at)}</div></div>
        <div>
          <div className="muted">Public URL</div>
          {instance.rootUrl
            ? <a href={instance.rootUrl} target="_blank" rel="noreferrer" className="mono">{instance.rootUrl}</a>
            : <span className="muted">-</span>}
        </div>
        {!compose && (
          <div>
            <div className="muted">Via hub proxy</div>
            {instance.proxyUrl
              ? <a href={instance.proxyUrl} target="_blank" rel="noreferrer" className="mono">{instance.proxyUrl}</a>
              : <span className="muted">-</span>}
          </div>
        )}
      </div>
    </Card>
  );
}

/**
 * Published endpoints of a compose service. Each row maps a compose service and
 * container port to a subdomain and, optionally, a domain the operator owns.
 */
function ExposuresCard({ instance, isAdmin, onChanged }: {
  instance: InstanceDetail;
  isAdmin: boolean;
  onChanged: () => Promise<void>;
}) {
  const [exposures, setExposures] = useState<Exposure[] | null>(null);
  const [services, setServices] = useState<ComposeServiceInfo[]>([]);
  const [service, setService] = useState('');
  const [port, setPort] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const flash = useFlash();

  const load = useCallback(async () => {
    try {
      const [list, detail] = await Promise.all([
        api.get<Exposure[]>(`/api/instances/${instance.id}/exposures`),
        api.get<InstanceComposeDetail>(`/api/instances/${instance.id}/compose`).catch(() => null),
      ]);
      setExposures(list);
      setServices(detail?.services ?? []);
    } catch (err) {
      flash.show(err instanceof Error ? err.message : 'Could not load endpoints', 'error');
    }
    // flash identity changes on every render; re-running on instance is enough
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance.id]);

  useEffect(() => { void load(); }, [load]);

  return (
    <Card style={{ marginBottom: '1.5rem' }}>
      <SectionTitle actions={flash.node}>Published endpoints</SectionTitle>
      <p className="muted" style={{ fontSize: '0.8rem', marginTop: 0 }}>
        Each endpoint publishes one service port on a subdomain. To also serve a domain you own,
        add it below and point an A record at this host.
      </p>

      {!exposures ? <Loading /> : exposures.length === 0 ? (
        <p className="muted" style={{ fontSize: '0.85rem' }}>No endpoints published.</p>
      ) : (
        <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '1rem' }}>
          {exposures.map((e) => (
            <ExposureRow
              key={e.id}
              instanceId={instance.id}
              exposure={e}
              isAdmin={isAdmin}
              onChanged={async () => { await load(); await onChanged(); }}
            />
          ))}
        </div>
      )}

      <SectionTitle>Add an endpoint</SectionTitle>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 12rem' }}>
          <label className="form-label">Service</label>
          {services.length > 0 ? (
            <select className="form-input mono" value={service} onChange={(e) => setService(e.target.value)}>
              <option value="">Select…</option>
              {services.map((s) => <option key={s.service} value={s.service}>{s.service}</option>)}
            </select>
          ) : (
            <input className="form-input mono" value={service} onChange={(e) => setService(e.target.value)} />
          )}
        </div>
        <div style={{ flex: '0 1 8rem' }}>
          <label className="form-label">Port</label>
          <input className="form-input mono" inputMode="numeric" value={port} onChange={(e) => setPort(e.target.value)} />
        </div>
        {isAdmin && (
          <div style={{ flex: '1 1 12rem' }}>
            <label className="form-label">Subdomain (optional)</label>
            <input className="form-input mono" value={subdomain} onChange={(e) => setSubdomain(e.target.value)} />
          </div>
        )}
        <ActionButton
          className="btn-primary"
          busyLabel="Publishing…"
          disabled={!service.trim() || !(Number(port) > 0)}
          onClick={async () => {
            await api.post(`/api/instances/${instance.id}/exposures`, {
              service: service.trim(),
              port: Number(port),
              ...(subdomain.trim() ? { subdomain: subdomain.trim() } : {}),
            });
            setService(''); setPort(''); setSubdomain('');
            flash.show('Endpoint published', 'ok');
            await load();
            await onChanged();
          }}
        >Publish</ActionButton>
      </div>
    </Card>
  );
}

function ExposureRow({ instanceId, exposure, isAdmin, onChanged }: {
  instanceId: number;
  exposure: Exposure;
  isAdmin: boolean;
  onChanged: () => Promise<void>;
}) {
  const [domain, setDomain] = useState(exposure.external_domain ?? '');
  const [instructions, setInstructions] = useState<DomainVerifyInstructions | null>(null);
  const flash = useFlash();
  const verified = exposure.external_domain_verified === 1;
  const base = `/api/instances/${instanceId}/exposures/${exposure.id}`;

  return (
    <div style={{ border: '1px solid var(--ha-border)', borderRadius: '0.5rem', padding: '0.75rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
        <span className="mono">{exposure.service}:{exposure.container_port}</span>
        {exposure.is_primary === 1 && <Pill tone="emerald">main</Pill>}
        <span className="muted" style={{ fontSize: '0.8rem' }}>→</span>
        <a href={exposure.url} target="_blank" rel="noreferrer" className="mono">{exposure.url}</a>
        {flash.node}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 14rem' }}>
          <label className="form-label">Custom domain</label>
          <input
            className="form-input mono" placeholder="habits.example.com"
            value={domain} onChange={(e) => setDomain(e.target.value)}
          />
        </div>
        <ActionButton
          className="btn-primary" busyLabel="Saving…"
          onClick={async () => {
            await api.patch(base, { externalDomain: domain.trim() || null });
            flash.show(
              isAdmin
                ? 'Domain saved — point an A record at this host'
                : 'Domain saved — add the TXT record and verify',
              'ok',
            );
            await onChanged();
          }}
        >Save</ActionButton>
        {exposure.external_domain && !verified && (
          <ActionButton
            busyLabel="Checking…"
            onClick={async () => {
              try {
                await api.post(`${base}/verify-domain`);
                setInstructions(null);
                flash.show('Domain verified', 'ok');
                await onChanged();
              } catch (err) {
                const payload = err as { message: string; instructions?: DomainVerifyInstructions };
                if (payload.instructions) setInstructions(payload.instructions);
                flash.show(payload.message, 'error');
                throw err;
              }
            }}
          >Verify</ActionButton>
        )}
        {exposure.is_primary !== 1 && (
          <ActionButton
            danger busyLabel="Removing…"
            confirm="Remove this endpoint? Its subdomain stops resolving."
            onClick={async () => {
              await api.delete(base);
              await onChanged();
            }}
          >Remove</ActionButton>
        )}
      </div>

      {exposure.external_domain && (
        <div style={{ marginTop: '0.5rem' }}>
          <Pill tone={verified ? 'emerald' : 'red'}>{verified ? 'domain live' : 'not verified'}</Pill>
        </div>
      )}

      {instructions && (
        <div style={{ marginTop: '0.5rem' }}>
          <Alert tone="warn">
            Add this DNS record, then verify again:
            <pre className="output" style={{ marginTop: '0.5rem', marginBottom: 0 }}>
{`${instructions.record_type}  ${instructions.host}  "${instructions.value}"`}
            </pre>
          </Alert>
        </div>
      )}
    </div>
  );
}

function ComposeFileCard({ instanceId }: { instanceId: number }) {
  const [detail, setDetail] = useState<InstanceComposeDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<InstanceComposeDetail>(`/api/instances/${instanceId}/compose`)
      .then(setDetail)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not read the stack'));
  }, [instanceId]);

  return (
    <Card style={{ marginBottom: '1.5rem' }}>
      <SectionTitle>Compose file</SectionTitle>
      {error && <Alert tone="error">{error}</Alert>}
      {!error && !detail && <Loading />}
      {detail && (
        <>
          <p className="muted" style={{ fontSize: '0.8rem', marginTop: 0 }}>
            Project <span className="mono">{detail.project}</span>. Published ports are added by the hub in a
            separate overlay file, so they are not shown here.
          </p>
          <pre className="output" style={{ marginBottom: 0, maxHeight: '24rem', overflow: 'auto' }}>{detail.composeYaml}</pre>
        </>
      )}
    </Card>
  );
}

function SubdomainCard({ instance, onChanged }: { instance: InstanceDetail; onChanged: () => Promise<void> }) {
  const [value, setValue] = useState(instance.subdomain);
  const flash = useFlash();

  return (
    <Card style={{ marginBottom: '1.5rem' }}>
      <SectionTitle actions={flash.node}>Subdomain</SectionTitle>
      <p className="muted" style={{ fontSize: '0.8rem', marginTop: 0 }}>
        Renaming recreates the container. The data volume is keyed to the instance, so nothing is lost.
      </p>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 14rem' }}>
          <input className="form-input mono" value={value} onChange={(e) => setValue(e.target.value)} />
        </div>
        <ActionButton
          className="btn-primary"
          busyLabel="Applying…"
          disabled={value === instance.subdomain}
          onClick={async () => {
            await api.post(`/api/instances/${instance.id}/subdomain`, { subdomain: value });
            flash.show('Subdomain updated', 'ok');
            await onChanged();
          }}
        >Rename</ActionButton>
      </div>
    </Card>
  );
}

function ExternalDomainCard({ instance, onChanged }: { instance: InstanceDetail; onChanged: () => Promise<void> }) {
  const [domain, setDomain] = useState(instance.external_domain ?? '');
  const [instructions, setInstructions] = useState<Record<string, string> | null>(null);
  const flash = useFlash();
  const verified = instance.external_domain_verified === 1;

  return (
    <Card style={{ marginBottom: '1.5rem' }}>
      <SectionTitle actions={flash.node}>Custom domain</SectionTitle>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <div style={{ flex: '1 1 16rem' }}>
          <label className="form-label">Domain</label>
          <input className="form-input mono" placeholder="app.example.com" value={domain} onChange={(e) => setDomain(e.target.value)} />
        </div>
        <ActionButton
          className="btn-primary" busyLabel="Saving…"
          onClick={async () => {
            await api.post(`/api/instances/${instance.id}/external-domain`, { domain });
            flash.show('Domain saved — now add the TXT record and verify', 'ok');
            await onChanged();
          }}
        >Save</ActionButton>
        {instance.external_domain && (
          <>
            <ActionButton
              busyLabel="Checking…"
              onClick={async () => {
                try {
                  await api.post(`/api/instances/${instance.id}/verify-domain`);
                  flash.show('Domain verified', 'ok');
                  setInstructions(null);
                  await onChanged();
                } catch (err) {
                  const payload = err as { message: string };
                  setInstructions({
                    name: `_codenteam-verify.${instance.external_domain}`,
                    type: 'TXT',
                    value: instance.domain_verify_token,
                  });
                  flash.show(payload.message, 'error');
                  throw err;
                }
              }}
            >Verify</ActionButton>
            <ActionButton
              danger busyLabel="Removing…"
              onClick={async () => {
                await api.delete(`/api/instances/${instance.id}/external-domain`);
                setDomain('');
                flash.show('Domain removed', 'ok');
                await onChanged();
              }}
            >Remove</ActionButton>
          </>
        )}
      </div>

      {instance.external_domain && (
        <Pill tone={verified ? 'emerald' : 'red'}>{verified ? 'verified' : 'not verified'}</Pill>
      )}

      {instructions && (
        <div style={{ marginTop: '0.75rem' }}>
          <Alert tone="warn">
            Add this DNS record, then verify again:
            <pre className="output" style={{ marginTop: '0.5rem', marginBottom: 0 }}>
{`${instructions['type']}  ${instructions['name']}  "${instructions['value']}"`}
            </pre>
          </Alert>
        </div>
      )}
    </Card>
  );
}

function ServiceUsersCard({ instanceId }: { instanceId: number }) {
  const [users, setUsers] = useState<ServiceUser[] | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const flash = useFlash();

  const load = useCallback(async () => {
    try {
      setUsers(await api.get<ServiceUser[]>(`/api/instances/${instanceId}/service-users`));
    } catch (err) {
      flash.show(err instanceof Error ? err.message : 'Could not load service users', 'error');
    }
  }, [instanceId, flash]);

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [instanceId]);

  return (
    <Card style={{ marginBottom: '1.5rem' }}>
      <SectionTitle actions={flash.node}>Service users</SectionTitle>
      <p className="muted" style={{ fontSize: '0.8rem', marginTop: 0 }}>
        Accounts inside the service. Adding or removing one recreates the container.
      </p>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <div style={{ flex: '1 1 10rem' }}>
          <label className="form-label">Username</label>
          <input className="form-input" value={username} onChange={(e) => setUsername(e.target.value)} />
        </div>
        <div style={{ flex: '1 1 10rem' }}>
          <label className="form-label">Password</label>
          <input className="form-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <ActionButton
          className="btn-primary" busyLabel="Adding…"
          disabled={!username || !password}
          onClick={async () => {
            await api.post(`/api/instances/${instanceId}/service-users`, { username, password });
            setUsername(''); setPassword('');
            flash.show('Service user added', 'ok');
            await load();
          }}
        >Add</ActionButton>
      </div>

      {!users ? <Loading /> : (
        <div className="scroll-x">
          <table className="data">
            <thead><tr><th>Username</th><th>Created</th><th>Actions</th></tr></thead>
            <tbody>
              {users.length === 0 ? <EmptyRow colSpan={3}>No service users.</EmptyRow> : users.map((u) => (
                <tr key={u.id}>
                  <td className="mono">{u.username}</td>
                  <td className="muted">{fmtDate(u.created_at)}</td>
                  <td>
                    <ActionButton
                      danger busyLabel="Removing…"
                      confirm={`Remove service user "${u.username}"?`}
                      onClick={async () => {
                        await api.delete(`/api/instances/${instanceId}/service-users/${u.id}`);
                        await load();
                      }}
                    >Remove</ActionButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function EnvVarsCard({ instanceId }: { instanceId: number }) {
  const [vars, setVars] = useState<EnvVar[] | null>(null);
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [revealed, setRevealed] = useState<Set<number>>(new Set());
  const flash = useFlash();

  const load = useCallback(async () => {
    try {
      setVars(await api.get<EnvVar[]>(`/api/instances/${instanceId}/env-vars`));
    } catch (err) {
      flash.show(err instanceof Error ? err.message : 'Could not load env vars', 'error');
    }
  }, [instanceId, flash]);

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [instanceId]);

  return (
    <Card style={{ marginBottom: '1.5rem' }}>
      <SectionTitle actions={flash.node}>Environment variables</SectionTitle>
      <p className="muted" style={{ fontSize: '0.8rem', marginTop: 0 }}>
        Injected on start. Changing one recreates the container. <span className="mono">NS_MANAGER_*</span> names are reserved.
      </p>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <div style={{ flex: '1 1 10rem' }}>
          <label className="form-label">Key</label>
          <input className="form-input mono" value={key} onChange={(e) => setKey(e.target.value)} />
        </div>
        <div style={{ flex: '1 1 14rem' }}>
          <label className="form-label">Value</label>
          <input className="form-input mono" value={value} onChange={(e) => setValue(e.target.value)} />
        </div>
        <ActionButton
          className="btn-primary" busyLabel="Saving…"
          disabled={!key}
          onClick={async () => {
            await api.post(`/api/instances/${instanceId}/env-vars`, { key, value });
            setKey(''); setValue('');
            flash.show('Variable saved', 'ok');
            await load();
          }}
        >Set</ActionButton>
      </div>

      {!vars ? <Loading /> : (
        <div className="scroll-x">
          <table className="data">
            <thead><tr><th>Key</th><th>Value</th><th>Actions</th></tr></thead>
            <tbody>
              {vars.length === 0 ? <EmptyRow colSpan={3}>No environment variables.</EmptyRow> : vars.map((v) => {
                const secret = SENSITIVE.test(v.key) && !revealed.has(v.id);
                return (
                  <tr key={v.id}>
                    <td className="mono">{v.key}</td>
                    <td className="mono">
                      {secret ? (
                        <button
                          type="button" className="btn-ghost btn-sm"
                          onClick={() => setRevealed((prev) => new Set(prev).add(v.id))}
                        >••••••  reveal</button>
                      ) : v.value}
                    </td>
                    <td>
                      <ActionButton
                        danger busyLabel="Removing…"
                        confirm={`Remove ${v.key}?`}
                        onClick={async () => {
                          await api.delete(`/api/instances/${instanceId}/env-vars/${v.id}`);
                          await load();
                        }}
                      >Remove</ActionButton>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function ResourceLimitsCard({ instance, onChanged }: { instance: InstanceDetail; onChanged: () => Promise<void> }) {
  const [cpu, setCpu] = useState(String(instance.cpu_limit));
  const [mem, setMem] = useState(String(instance.memory_limit_mb));
  const [swap, setSwap] = useState(String(instance.memory_swap_limit_mb));
  const flash = useFlash();

  return (
    <Card style={{ marginBottom: '1.5rem' }}>
      <SectionTitle actions={flash.node}>Resource limits (admin)</SectionTitle>
      <p className="muted" style={{ fontSize: '0.8rem', marginTop: 0 }}>Applying recreates the container.</p>
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 8rem' }}>
          <label className="form-label">CPU cores</label>
          <input className="form-input" type="number" step="0.1" min="0.1" value={cpu} onChange={(e) => setCpu(e.target.value)} />
        </div>
        <div style={{ flex: '1 1 8rem' }}>
          <label className="form-label">Memory (MB)</label>
          <input className="form-input" type="number" min="64" value={mem} onChange={(e) => setMem(e.target.value)} />
        </div>
        <div style={{ flex: '1 1 8rem' }}>
          <label className="form-label">Memory + swap (MB)</label>
          <input className="form-input" type="number" min="64" value={swap} onChange={(e) => setSwap(e.target.value)} />
        </div>
        <ActionButton
          className="btn-primary" busyLabel="Applying…"
          onClick={async () => {
            await api.patch(`/api/instances/${instance.id}/resource-limits`, {
              cpu_limit: Number(cpu),
              memory_limit_mb: Number(mem),
              memory_swap_limit_mb: Number(swap),
            });
            flash.show('Limits applied', 'ok');
            await onChanged();
          }}
        >Apply</ActionButton>
      </div>
    </Card>
  );
}

function ExpirationCard({ instance, onChanged }: { instance: InstanceDetail; onChanged: () => Promise<void> }) {
  // The API stores expiry in SECONDS; datetime-local wants a local ISO string.
  const initial = instance.expires_at
    ? new Date(instance.expires_at * 1000).toISOString().slice(0, 16)
    : '';
  const [when, setWhen] = useState(initial);
  const flash = useFlash();

  return (
    <Card style={{ marginBottom: '1.5rem' }}>
      <SectionTitle actions={flash.node}>Expiration (admin)</SectionTitle>
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 14rem' }}>
          <label className="form-label">Expires at (blank = never)</label>
          <input className="form-input" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
        </div>
        <ActionButton
          className="btn-primary" busyLabel="Saving…"
          onClick={async () => {
            await api.patch(`/api/instances/${instance.id}/expiration`, {
              expires_at: when ? Math.floor(new Date(when).getTime() / 1000) : null,
            });
            flash.show('Expiration saved', 'ok');
            await onChanged();
          }}
        >Save</ActionButton>
      </div>
    </Card>
  );
}

function DangerZoneCard({ instance, onDeleted }: { instance: InstanceDetail; onDeleted: () => void }) {
  return (
    <Card style={{ borderColor: 'var(--ha-accent-red)' }}>
      <SectionTitle>Danger zone</SectionTitle>
      <p className="muted" style={{ fontSize: '0.8rem', marginTop: 0 }}>
        Deleting removes the container, its data volume and its routes. This cannot be undone.
      </p>
      <ActionButton
        danger className="btn-ghost" busyLabel="Deleting…"
        confirm={`Delete ${instance.name}? The container and all of its data are removed permanently.`}
        onClick={async () => {
          await api.delete(`/api/instances/${instance.id}`);
          onDeleted();
        }}
      >Delete this service</ActionButton>
    </Card>
  );
}
